import React, { useState, useEffect, useCallback } from "react";
import { crmFetch, formatMoney, toInputDate } from "./crmApi";

const BLANK_ITEM = { label: "", amount: "" };

/**
 * Modal for building a lead's quote. Start from a saved package (one tap
 * loads its line items), tweak anything, and optionally save the result back
 * as a new package for next time. Saving sends the quote: the lead advances
 * to Quote Sent and the share link is ready to copy.
 */
function QuoteBuilder({ lead, existingQuote, onClose, onSaved }) {
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState(
    existingQuote?.items?.length ? existingQuote.items.map((i) => ({ ...i })) : [{ ...BLANK_ITEM }]
  );
  const [title, setTitle] = useState(
    existingQuote?.title || (lead.event_type ? `${lead.event_type} package` : "")
  );
  const [discount, setDiscount] = useState(existingQuote?.discount > 0 ? existingQuote.discount : "");
  const [validUntil, setValidUntil] = useState(toInputDate(existingQuote?.valid_until));
  const [notes, setNotes] = useState(existingQuote?.notes || "");
  const [saveAsPackage, setSaveAsPackage] = useState(false);
  const [packageName, setPackageName] = useState("");
  const [activePackageId, setActivePackageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPackages = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/packages");
      setPackages(d.packages || []);
    } catch { /* the builder works fine without saved packages */ }
  }, []);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const applyPackage = (pkg) => {
    setItems(pkg.items.map((i) => ({ ...i })));
    setActivePackageId(pkg.id);
    if (!title.trim()) setTitle(pkg.name);
  };

  const deletePackage = async (pkg, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the "${pkg.name}" package? Quotes already sent are not affected.`)) return;
    try {
      await crmFetch(`/api/crm/packages/${pkg.id}`, { method: "DELETE" });
      if (activePackageId === pkg.id) setActivePackageId(null);
      loadPackages();
    } catch (err) {
      setError(err.message);
    }
  };

  const setItem = (idx, key, value) => {
    setItems((list) => list.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
    setActivePackageId(null);
  };

  const addItem = () => setItems((list) => [...list, { ...BLANK_ITEM }]);

  const removeItem = (idx) => {
    setItems((list) => (list.length > 1 ? list.filter((_, i) => i !== idx) : list));
    setActivePackageId(null);
  };

  const cleanItems = items
    .map((i) => ({ label: String(i.label || "").trim(), amount: Number(i.amount) || 0 }))
    .filter((i) => i.label !== "");
  const subtotal = cleanItems.reduce((s, i) => s + i.amount, 0);
  const total = Math.max(0, subtotal - (Number(discount) || 0));

  const save = async () => {
    if (cleanItems.length === 0) {
      setError("Add at least one line item with a name.");
      return;
    }
    if (saveAsPackage && !packageName.trim()) {
      setError("Give the new package a name, or untick \"Save as package\".");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (saveAsPackage) {
        await crmFetch("/api/crm/packages", {
          method: "POST",
          body: { name: packageName, items: cleanItems },
        });
      }
      const d = await crmFetch(`/api/crm/leads/${lead.id}/quote`, {
        method: "POST",
        body: {
          title, items: cleanItems, discount: Number(discount) || 0,
          valid_until: validUntil || null, notes,
        },
      });
      onSaved(d.quote);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal crm-quote-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{existingQuote ? "Edit quote" : "＋ Create quote"} for {lead.client_name}</h3>
        {error && <div className="crm-form-error">{error}</div>}

        {packages.length > 0 && (
          <div className="crm-pkg-row">
            <span className="crm-pkg-label">Start from a package:</span>
            {packages.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`crm-pkg-chip ${activePackageId === p.id ? "active" : ""}`}
                onClick={() => applyPackage(p)}
                title={`${p.items.length} items · ${formatMoney(p.items.reduce((s, i) => s + Number(i.amount || 0), 0)) || ""}`}
              >
                📦 {p.name}
                <span className="crm-pkg-del" onClick={(e) => deletePackage(p, e)} title="Delete package">✕</span>
              </button>
            ))}
          </div>
        )}

        <div className="crm-field full" style={{ marginBottom: 12 }}>
          <label>Quote title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Wedding Gold package" />
        </div>

        <div className="crm-quote-items">
          <div className="crm-quote-items-head">
            <span>Item</span>
            <span>Amount</span>
            <span />
          </div>
          {items.map((it, idx) => (
            <div className="crm-quote-item-row" key={idx}>
              <input
                value={it.label}
                onChange={(e) => setItem(idx, "label", e.target.value)}
                placeholder={idx === 0 ? "e.g. Full-day candid photography" : "Add another item…"}
              />
              <input
                type="number"
                min="0"
                value={it.amount}
                onChange={(e) => setItem(idx, "amount", e.target.value)}
                placeholder="0"
              />
              <button type="button" className="crm-task-del" onClick={() => removeItem(idx)} title="Remove item">✕</button>
            </div>
          ))}
          <button type="button" className="crm-link-btn" onClick={addItem}>＋ Add item</button>
        </div>

        <div className="crm-quote-totals">
          <div className="crm-kv"><span className="k">Subtotal</span><span className="v">{formatMoney(subtotal) || "—"}</span></div>
          <div className="crm-quote-discount">
            <span className="k">Discount</span>
            <input
              type="number" min="0" value={discount}
              onChange={(e) => setDiscount(e.target.value)} placeholder="0"
            />
          </div>
          <div className="crm-kv crm-quote-grand"><span className="k">Total</span><span className="v">{formatMoney(total) || "0"}</span></div>
        </div>

        <div className="crm-form-grid" style={{ marginTop: 12 }}>
          <div className="crm-field">
            <label>Valid until</label>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>
          <div className="crm-field">
            <label className="crm-checkline">
              <input
                type="checkbox"
                checked={saveAsPackage}
                onChange={(e) => setSaveAsPackage(e.target.checked)}
              />
              Save these items as a package
            </label>
            {saveAsPackage && (
              <input
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder="Package name, e.g. Wedding Gold"
              />
            )}
          </div>
          <div className="crm-field full">
            <label>Notes for the client</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Terms, what's included, advance expected…"
            />
          </div>
        </div>

        <div className="crm-modal-actions">
          <button type="button" className="crm-btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="crm-btn primary" disabled={saving} onClick={save}>
            {saving ? "Saving…" : existingQuote ? "Save changes" : "Save & get link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuoteBuilder;
